import { faGear } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'Tooltip';
import { Dropdown } from 'Dropdown';
import { useClickAway } from 'react-use';
import { type TFunction } from 'i18next';
import { AudioControlBarButton } from './AudioControlBarButton';

type Props = {
    activeDeviceId: string;
    onSelect: (deviceId: string) => void;
    devices: MediaDeviceInfo[];
};

function getDeviceLabel({ device, t }: { device: MediaDeviceInfo; t: TFunction }) {
    if (device.deviceId === 'default' && !device.label) return t('default');
    return device.label || t('audioDevice');
}

export function MicrophoneSelectButton({ activeDeviceId, onSelect, devices }: Props) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'TutorBotVoiceAgent.audioControlBar' });
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useClickAway(tooltipRef, () => {
        if (dropdownOpen) {
            setTimeout(() => {
                setDropdownOpen(false);
            }, 150);
        }
    });

    const handleMicrophoneSelect = useCallback(
        (selectedKeys: string[]) => {
            onSelect(selectedKeys[0]);
            setTimeout(() => {
                setDropdownOpen(false);
            }, 150);
        },
        [onSelect],
    );

    const dropdownItems = useMemo(
        () =>
            devices
                // We believe the "Headset earpiece" option is a virtual audio device exposed by Android that doesn't
                // map to an actual physical headset, so we're proactively removing it from the devices list.
                // More info on this ticket: https://trello.com/c/e48IcQv7
                .filter(device => device.label !== 'Headset earpiece')
                .map(device => ({
                    key: device.deviceId,
                    label: getDeviceLabel({ device, t }),
                    isReadOnly: activeDeviceId === device.deviceId,
                })),
        [devices, activeDeviceId, t],
    );

    if (devices.length === 1) return null;

    return (
        <Tooltip
            content={t('microphoneSelect')}
            classNames={{ base: dropdownOpen ? 'opacity-0' : undefined }}
            placement="top"
        >
            <div ref={tooltipRef}>
                <Dropdown
                    items={dropdownItems}
                    selectedKeys={[activeDeviceId]}
                    selectionMode="single"
                    onSelectionChange={handleMicrophoneSelect}
                    placement="top"
                    isOpen={dropdownOpen}
                >
                    <div>
                        <AudioControlBarButton
                            onPress={() => setDropdownOpen(prev => !prev)}
                            icon={<FontAwesomeIcon icon={faGear} className="text-2xl sm:text-md" />}
                            label={t('microphoneSelect')}
                        />
                    </div>
                </Dropdown>
            </div>
        </Tooltip>
    );
}
